import { cloneElement, ReactElement, ReactNode } from "react";
import {
  Button,
  ButtonProps,
  Popover as HeroUIPopover,
  PopoverContent,
  PopoverTrigger,
  PopoverProps as HeroUIPopoverProps,
} from "@heroui/react";
import { DotsThreeVerticalIcon, IconProps } from "@phosphor-icons/react";
import { useOpenState } from "@/shared/hooks/useOpenState";

interface PopoverProps extends Omit<HeroUIPopoverProps, "children"> {
  children: ReactNode;
  icon?: ReactElement<IconProps>;
  id: string;
  externalState?: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
  style?: React.CSSProperties;
  triggerProps?: {
    isDisabled?: boolean;
    size?: ButtonProps["size"];
  };
}

export function Popover({ children, icon, id, externalState, style, triggerProps, ...rest }: PopoverProps) {
  const { isOpen, handleOpenChange } = useOpenState({ id, externalState });
  const iconElement = icon ? (
    cloneElement(icon, { size: 24, ...icon?.props })
  ) : (
    <DotsThreeVerticalIcon size={24} weight="bold" />
  );

  return (
    <HeroUIPopover
      isOpen={isOpen}
      shouldCloseOnBlur={false}
      shouldCloseOnEscape={false}
      shouldCloseOnScroll={false}
      placement="bottom-end"
      className="w-full"
      style={{ ...style }}
      onOpenChange={handleOpenChange}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      <PopoverTrigger>
        <Button isIconOnly variant="light" {...triggerProps}>
          {iconElement}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2" aria-label="Note actions">
        <div className="w-full">{children}</div>
      </PopoverContent>
    </HeroUIPopover>
  );
}
